import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMyClients } from 'src/api/customer';
import { getStorage, useLocalStorage } from 'src/hooks/use-local-storage';
import { CONSTANT_VARIABLES } from 'src/constants/constants-variables';
import { AUTH_STORAGE_KEYS } from 'src/utils/storage-available';
import { canActAsClient, isRoleCustomer } from 'src/utils/check-user-is-admin';

import { ActingClientContext } from './acting-client-context';
import {
  afterRefusal,
  resolveActingClient,
  type ActingRole,
  type Grant,
} from './acting-client-state';
import {
  ACTING_CLIENT_REFUSED_EVENT,
  readPreference,
  readTabSelection,
  writePreference,
  writeTabSelection,
} from './acting-client-storage';

type Props = { children: React.ReactNode };

/** The login snapshot's venue as a one-element grant list, or none. */
const snapshotGrants = (): Grant[] => {
  const user = getStorage(CONSTANT_VARIABLES.USER_STORAGE_KEY) as {
    clientId?: string | number | null;
    clientName?: string | null;
    clientSlug?: string | null;
  } | null;
  return user?.clientId
    ? [{ id: String(user.clientId), name: user.clientName ?? null, slug: user.clientSlug ?? null }]
    : [];
};

const actingRole = (): ActingRole => {
  if (isRoleCustomer()) return 'customer';
  if (canActAsClient()) return 'acting';
  return 'other';
};

/** The agent picker's stored value, as the scalar id it holds — or nothing. */
const legacySelection = (value: unknown): string | null =>
  value != null && value !== '' ? String(value) : null;

/**
 * NAT-2497 — one owner of "which venue am I acting for".
 *
 * A customer's venues come from `GET /customer/my-clients` and the selection
 * is this tab's, seeded from a startup preference. An agent or admin keeps the
 * picker it already had: its selection stays in the shared key the catalogue
 * writes, and it is never gated. The checkout context reads its
 * `selectedAgentClientId` from here for every role, so the cart, checkout
 * and catalogue plumbing that already sends that id sends the right one.
 *
 * A refusal from the server (the transport dispatches it on a 403 that
 * carried a token) reloads the grants and drops a selection they no longer
 * contain, so the app re-gates instead of retrying a venue that was revoked.
 */
export function ActingClientProvider({ children }: Props) {
  const role = actingRole();

  const {
    data: fetched,
    error: grantsError,
    isValidating,
    refetch,
  } = useMyClients(role === 'customer');
  // A backend without the grants route (404: this frontend deployed ahead of
  // it) is "feature absent", not an error: the login snapshot's venue is the
  // sole grant, exactly what the server would have answered.
  const grants = useMemo(() => {
    if (fetched !== undefined) return fetched;
    if ((grantsError as { response?: { status?: number } })?.response?.status !== 404)
      return undefined;
    return snapshotGrants();
  }, [fetched, grantsError]);

  // The acting roles' selection, in the key the catalogue picker writes.
  const { state: legacy, update: legacyUpdate } = useLocalStorage(
    AUTH_STORAGE_KEYS.selectedAgentClientId,
    {},
  );

  const [tab, setTab] = useState<string | null>(readTabSelection);
  const [preference, setPreference] = useState<string | null>(readPreference);
  // Bumped after a reload so a consumer of the mocked-or-cached grants re-reads them.
  const [, setReloads] = useState(0);

  const stored = role === 'acting' ? legacySelection(legacy?.selectedAgentClientId) : tab;

  // A refusal says the grants on hand are suspect. SWR keeps serving the last
  // successful list when a revalidation fails, so without this a reload that
  // FAILS after a revocation would leave the provider `ready` on the very
  // venue that was just taken away. Until a reload succeeds, that state is the
  // error gate — which offers retry — rather than a working page.
  const [suspect, setSuspect] = useState(false);
  useEffect(() => {
    if (!isValidating && !grantsError && fetched !== undefined) setSuspect(false);
  }, [isValidating, grantsError, fetched]);

  const resolution = useMemo(
    () =>
      resolveActingClient({
        role,
        grants,
        stored,
        preference,
        error: !!grantsError && (grants === undefined || suspect),
      }),
    [role, grants, stored, preference, grantsError, suspect],
  );

  const setActingClientId = useCallback(
    (id: string | null) => {
      if (role === 'acting') {
        legacyUpdate('selectedAgentClientId', id ?? '');
        return;
      }
      writeTabSelection(id);
      setTab(id);
      if (id) {
        writePreference(id);
        setPreference(id);
      }
    },
    [role, legacyUpdate],
  );

  const refreshGrants = useCallback(() => {
    Promise.resolve(refetch()).finally(() => setReloads((n) => n + 1));
  }, [refetch]);

  // A refusal reloads the grants — once per refusal, never while a reload is
  // already in flight (a burst of 403s from one page must not fan out).
  const validatingRef = useRef(isValidating);
  validatingRef.current = isValidating;
  useEffect(() => {
    if (role !== 'customer') return undefined;
    const onRefused = () => {
      setSuspect(true);
      if (validatingRef.current) return;
      validatingRef.current = true; // synchronously: N refusals in one tick reload once
      refreshGrants();
    };
    window.addEventListener(ACTING_CLIENT_REFUSED_EVENT, onRefused);
    return () => window.removeEventListener(ACTING_CLIENT_REFUSED_EVENT, onRefused);
  }, [role, refreshGrants]);

  // A selection resolved from the preference, or the sole venue, becomes THIS
  // tab's: another tab changing the preference must not move this one on its
  // next reload (spec §6, per-tab ownership).
  useEffect(() => {
    if (role !== 'customer' || resolution.status !== 'ready' || !resolution.clientId) return;
    if (tab !== resolution.clientId) {
      writeTabSelection(resolution.clientId);
      setTab(resolution.clientId);
    }
  }, [role, resolution.status, resolution.clientId, tab]);

  // Whenever the grants settle, a selection they no longer contain is dropped.
  useEffect(() => {
    if (role !== 'customer' || grants === undefined) return;
    const kept = afterRefusal({ grants, current: tab });
    if (kept !== tab) {
      writeTabSelection(kept);
      setTab(kept);
    }
  }, [role, grants, tab]);

  const value = useMemo(
    () => ({
      role,
      status: resolution.status,
      actingClientId: resolution.clientId,
      grants: grants ?? [],
      showSelector: resolution.showSelector,
      setActingClientId,
      refreshGrants,
    }),
    [role, resolution, grants, setActingClientId, refreshGrants],
  );

  return <ActingClientContext.Provider value={value}>{children}</ActingClientContext.Provider>;
}
